import { safeLocalStorage } from "@/utils/safeStorage";
import { useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { toast } from 'sonner';

const STREAK_THRESHOLD = 7;
const MAX_FREEZE_TOKENS = 5;
const DAYS_BETWEEN_REWARDS = 7;

export const useStreakTokenRegeneration = () => {
  const { user } = useAuth();

  const checkAndAwardToken = useCallback(async () => {
    if (!user) return;

    try {
      // Fetch streak data with last token award date
      const { data: streakData, error } = await supabase
        .from('user_streaks')
        .select('current_streak, freeze_tokens, updated_at, last_active_date')
        .eq('user_id', user.id)
        .single();

      if (error || !streakData) return;

      // Check if streak meets threshold
      if (streakData.current_streak < STREAK_THRESHOLD) return;

      // Check if at max tokens
      if (streakData.freeze_tokens >= MAX_FREEZE_TOKENS) return;

      // Calculate days since last activity (proxy for token award check)
      const lastActive = streakData.last_active_date 
        ? new Date(streakData.last_active_date) 
        : new Date();
      
      // Use localStorage to track last token award (client-side persistence)
      const lastAwardKey = `streak_token_award_${user.id}`;
      const lastAwardDate = safeLocalStorage.getItem(lastAwardKey);
      const now = new Date();

      if (lastAwardDate) {
        const daysSinceAward = Math.floor(
          (now.getTime() - new Date(lastAwardDate).getTime()) / (1000 * 60 * 60 * 24)
        );

        if (daysSinceAward < DAYS_BETWEEN_REWARDS) return;
      }

      // Award a new freeze token
      const { error: updateError } = await supabase
        .from('user_streaks')
        .update({
          freeze_tokens: streakData.freeze_tokens + 1,
        })
        .eq('user_id', user.id);

      if (updateError) {
        console.error('Error awarding freeze token:', updateError);
        return;
      }

      // Store the award date
      safeLocalStorage.setItem(lastAwardKey, now.toISOString());

      toast.success(
        '🧊 Streak Reward! You earned a freeze token for maintaining your 7+ day streak!',
        { duration: 5000 }
      );

    } catch (error) {
      console.error('Error checking streak token regeneration:', error);
    }
  }, [user]);

  useEffect(() => {
    // Check on mount
    checkAndAwardToken();

    // Also check periodically (every hour while app is open)
    const interval = setInterval(checkAndAwardToken, 60 * 60 * 1000);

    return () => clearInterval(interval);
  }, [checkAndAwardToken]);

  return { checkAndAwardToken };
};
